'use client';

import React, { useEffect, useRef, useState } from 'react';
import WeddingLandingPage from './WeddingLandingPage';

type IntroStage = 'welcome' | 'playing' | 'revealed';

const experienceStyles = `
  @keyframes vimi-intro-pulse {
    0%, 100% { transform: scale(1); box-shadow: 0 18px 60px rgba(108, 66, 43, 0.2); }
    50% { transform: scale(1.035); box-shadow: 0 22px 72px rgba(201, 169, 110, 0.42); }
  }

  @keyframes vimi-intro-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .vimi-logo-button {
    animation: vimi-intro-pulse 2.8s ease-in-out infinite;
  }

  .vimi-intro-fade {
    animation: vimi-intro-fade-in 0.7s ease both;
  }

  @media (prefers-reduced-motion: reduce) {
    .vimi-logo-button,
    .vimi-intro-fade {
      animation: none;
    }
  }
`;

export default function InvitationExperience() {
  const [stage, setStage] = useState<IntroStage>('welcome');
  const [isMuted, setIsMuted] = useState(false);
  const [playbackMessage, setPlaybackMessage] = useState('');
  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    document.body.style.overflow = stage === 'revealed' ? '' : 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, [stage]);

  useEffect(() => {
    if (stage !== 'playing') return;

    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    video.play().catch(() => {
      setPlaybackMessage(
        'The introduction video could not start automatically. Tap the video to play it, or use Skip.'
      );
    });
  }, [stage]);

  const revealInvitation = () => {
    videoRef.current?.pause();
    setStage('revealed');
    window.scrollTo({ top: 0 });
  };

  const startExperience = async () => {
    setPlaybackMessage('');
    setStage('playing');

    const audio = audioRef.current;

    if (audio) {
      audio.volume = 0.65;
      audio.muted = isMuted;
    }

    try {
      await audio?.play();
    } catch {
      setPlaybackMessage(
        'Music was blocked by your browser. Use the music button to try again.'
      );
    }
  };

  const toggleMusic = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    const nextMuted = !isMuted;
    audio.muted = nextMuted;
    setIsMuted(nextMuted);

    if (!nextMuted && audio.paused) {
      try {
        await audio.play();
      } catch {
        // A later visitor interaction can resume audio if the browser blocks it.
      }
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#fdf6f0' }}>
      <style dangerouslySetInnerHTML={{ __html: experienceStyles }} />

      <audio
        ref={audioRef}
        src="/assets/background.mp3"
        loop
        preload="auto"
        aria-hidden="true"
      />

      {stage === 'welcome' && (
        <section
          className="fixed inset-0 z-50 flex min-h-[100svh] items-center justify-center overflow-hidden px-6 py-10 text-center"
          style={{
            background:
              'radial-gradient(circle at 50% 35%, #fffaf4 0%, #fdf1e6 55%, #f6dfcf 100%)',
          }}
          aria-labelledby="vimi-welcome-title"
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 20%, rgba(201,169,110,.22) 0 2px, transparent 3px), radial-gradient(circle at 80% 75%, rgba(212,160,176,.2) 0 2px, transparent 3px)',
              backgroundSize: '54px 54px, 70px 70px',
            }}
          />

          <div className="relative z-10 mx-auto flex w-full max-w-md flex-col items-center vimi-intro-fade">
            <p
              className="mb-3 text-xs font-semibold uppercase tracking-[0.32em]"
              style={{ color: '#c09a5a' }}
            >
              Vikas &amp; Anamika
            </p>
            <h1
              id="vimi-welcome-title"
              className="font-display text-3xl font-medium leading-tight sm:text-4xl"
              style={{ color: '#3d2b1f' }}
            >
              Will you join us for <span style={{ color: '#b78c4f' }}>#Vimi?</span>
            </h1>

            <button
              type="button"
              onClick={startExperience}
              className="vimi-logo-button my-8 flex h-48 w-48 items-center justify-center rounded-full border-0 bg-transparent p-0 transition-transform focus:outline-none focus-visible:ring-4 focus-visible:ring-[#c9a96e]/50 sm:h-56 sm:w-56"
              aria-label="Open Vimi's wedding invitation"
            >
              <img
                src="/assets/logo-button.svg"
                alt=""
                width="224"
                height="224"
                className="h-full w-full object-contain"
              />
            </button>

            <p className="text-sm font-medium tracking-wide" style={{ color: '#765b4b' }}>
              Tap the logo to open your invitation
            </p>
            <p className="mt-3 max-w-xs text-xs leading-relaxed" style={{ color: '#9b7b6b' }}>
              The invitation begins with music. You can mute it at any time.
            </p>
          </div>
        </section>
      )}

      {stage === 'playing' && (
        <section
          className="fixed inset-0 z-50 flex min-h-[100svh] items-center justify-center bg-black vimi-intro-fade"
          aria-label="Vimi wedding introduction"
        >
          <video
            ref={videoRef}
            src="/assets/intro-video.mp4"
            autoPlay
            muted
            playsInline
            preload="auto"
            onClick={() => videoRef.current?.play().catch(() => {})}
            onEnded={revealInvitation}
            onError={revealInvitation}
            className="h-full w-full object-contain"
            aria-label="Vimi wedding invitation video"
          />

          <div className="absolute right-4 top-4 flex items-center gap-2">
            <button
              type="button"
              onClick={toggleMusic}
              className="rounded-full border border-white/30 bg-black/50 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm"
              aria-label={isMuted ? 'Turn music on' : 'Mute music'}
            >
              {isMuted ? 'Music off' : 'Music on'}
            </button>
            <button
              type="button"
              onClick={revealInvitation}
              className="rounded-full border border-white/30 bg-black/50 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm"
            >
              Skip
            </button>
          </div>

          {playbackMessage && (
            <p
              className="absolute bottom-8 left-1/2 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-xl bg-black/60 px-4 py-3 text-center text-sm text-white"
              role="status"
            >
              {playbackMessage}
            </p>
          )}
        </section>
      )}

      <div
        className={stage === 'revealed' ? 'vimi-intro-fade' : 'invisible h-screen overflow-hidden'}
        aria-hidden={stage !== 'revealed'}
      >
        <WeddingLandingPage />
      </div>

      {stage === 'revealed' && (
        <button
          type="button"
          onClick={toggleMusic}
          className="fixed bottom-5 right-5 z-40 flex h-12 items-center gap-2 rounded-full px-4 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-105 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#c9a96e]/40"
          style={{
            background: 'linear-gradient(135deg, #9b6b4d 0%, #c9a96e 100%)',
          }}
          aria-label={isMuted ? 'Turn background music on' : 'Mute background music'}
          title={isMuted ? 'Turn music on' : 'Mute music'}
        >
          <span aria-hidden="true">{isMuted ? '🔇' : '♫'}</span>
          <span>{isMuted ? 'Music off' : 'Music on'}</span>
        </button>
      )}
    </div>
  );
}
